// Pure validation for the TR-605 transfer dialog's journal-number field (feature 013).
// Kept out of the dialog component so React Fast Refresh stays component-only and the rule is
// unit-testable in isolation (mirrors `student-lifecycle.ts`).

import type { V1ClassesListParams } from '@/api/model'

// TR-1002 canonical-login bound — the same ceiling the backend allocator and DB checks enforce.
export const JOURNAL_NUMBER_MAX = 99999

/**
 * Search active classes in the source account’s program. There is deliberately no
 * `school` filter: R3 also covers cross-school transfers. The explicit search
 * term makes every target reachable even when the global list exceeds one page.
 */
export function transferTargetQuery(
  search: string,
  sourceIsOpenProgram: boolean,
): V1ClassesListParams {
  return {
    state: 'active',
    school_is_open_program: sourceIsOpenProgram,
    search: search.trim() || undefined,
    page_size: 100,
  }
}

/**
 * Validate the operator-chosen journal number for a transfer (owner correction 2026-07-12):
 * the operator may pick ANY free number in `1..JOURNAL_NUMBER_MAX`, including a mid-range hole
 * left by an earlier departure; only a number already occupied in the target class is refused.
 *
 * Returns a Polish error message when the number is out of range or already taken in the target
 * class, or `null` when it is a valid free choice. `occupied` is the target class's set of used
 * journal numbers (from the backend transfer-preview). The server re-checks and is authoritative;
 * this is the fast client guard that disables confirm before a doomed request.
 */
export function journalNumberError(value: number | undefined, occupied: number[]): string | null {
  if (value === undefined || !Number.isInteger(value) || value < 1 || value > JOURNAL_NUMBER_MAX) {
    return `Podaj numer w dzienniku z zakresu 1–${JOURNAL_NUMBER_MAX}.`
  }
  if (occupied.includes(value)) {
    return 'Ten numer w dzienniku jest już zajęty w klasie docelowej.'
  }
  return null
}
